import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ProfileCategoryCountsResponse, ProfileSort } from '@shared/schemas';

import type { FilterGroup } from '@/types/filters';

import type { AiSearchResult, ProfilesPage } from '../infrastructure/my-profiles.gateway';
import { type FacetOptions, initialState } from './my-profiles.state';

/**
 * Client HNWIs list slice. Reducers are pure state transitions only; the
 * paginated fetch lives in the saga, which reads `page` / `pageSize` / `filters`
 * back from this state and dispatches these transitions as it progresses.
 * `filtersSet` / `pageSizeSet` / `sortSet` reset to the first page (mirroring the old
 * `useInfiniteApi`); the saga triggers the refetch.
 */
const slice = createSlice({
  name: 'myProfiles',
  initialState,
  reducers: {
    // ── Cursor + filters (the saga refetches after applying these) ──
    pageSet: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    pageSizeSet: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.page = 0;
    },
    filtersSet: (state, action: PayloadAction<FilterGroup | null>) => {
      state.filters = action.payload;
      state.page = 0;
    },
    searchSet: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.page = 0;
    },
    sortSet: (state, action: PayloadAction<ProfileSort | null>) => {
      state.sort = action.payload;
      state.page = 0;
    },

    // ── Load lifecycle ──
    loadStarted: (state) => {
      state.loading = true;
      state.error = null;
    },
    loadSucceeded: (state, action: PayloadAction<ProfilesPage>) => {
      state.rows = action.payload.rows;
      state.total = action.payload.total;
      state.loading = false;
    },
    loadFailed: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // ── Ask AI (#567) ──
    aiSearchStarted: (state) => {
      state.aiSearch.status = 'asking';
      state.aiSearch.error = null;
    },
    aiSearchAnswered: (state, action: PayloadAction<AiSearchResult & { query: string }>) => {
      state.aiSearch.status = 'idle';
      state.aiSearch.answer = action.payload;
      state.aiSearch.answerId += 1;
    },
    aiSearchFailed: (state, action: PayloadAction<string>) => {
      state.aiSearch.status = 'failed';
      state.aiSearch.error = action.payload;
    },
    /** The interpretation line was closed, or its rules undone. */
    aiSearchDismissed: (state) => {
      state.aiSearch.status = 'idle';
      state.aiSearch.error = null;
      state.aiSearch.answer = null;
    },

    // ── Category band (#553) ──
    categoryCountsLoaded: (state, action: PayloadAction<ProfileCategoryCountsResponse>) => {
      state.categoryCounts = action.payload;
    },
    /**
     * A failed fetch clears the band rather than freezing the last figures.
     *
     * Stale counts beside fresh rows are worse than none: the tiles would keep
     * claiming a population nobody re-checked, and there is no way for a reader
     * to tell. The band simply does not render.
     */
    categoryCountsCleared: (state) => {
      state.categoryCounts = null;
    },

    // ── Licence scope (PI-117) ──
    scopeSet: (state, action: PayloadAction<string>) => {
      state.licenceId = action.payload;
    },

    // ── Facet choices (#615) ──
    facetOptionsLoaded: (state, action: PayloadAction<FacetOptions>) => {
      state.facetOptions = action.payload;
    },
  },
});

export const myProfilesActions = slice.actions;
export const myProfilesReducer = slice.reducer;
